import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, CreditCard, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useCart } from '../context/CartContext';
import axios from 'axios';
import { getProxiedImageUrl } from '../utils/helpers';
import { trackBeginCheckout } from '../lib/gads';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Checkout = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const { cart, clearCart } = useCart();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [orderId, setOrderId] = useState(null);
  const [paymentConfig, setPaymentConfig] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    province: '',
    postal_code: '',
    country: 'IT',
    notes: ''
  });
  
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const [transferType, setTransferType] = useState('standard');
  const [codAccepted, setCodAccepted] = useState(false);
  
  const [cartTotals, setCartTotals] = useState(null);

  // Coupon state
  const [couponCode, setCouponCode] = useState('');
  const [couponData, setCouponData] = useState(null); // validated coupon
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState('');

  // Load payment config
  useEffect(() => {
    const fetchPaymentConfig = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/payments/config`);
        setPaymentConfig(response.data);
      } catch (err) {
      }
    };
    fetchPaymentConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Calculate cart totals
  useEffect(() => {
    const calculateTotals = async () => {
      if (cart.length === 0) return;
      
      try {
        const cartItems = cart.map(item => ({
          sku: item.sku,
          quantity: item.quantity
        }));
        
        const response = await axios.post(
          `${API_URL}/api/cart/calculate`,
          { items: cartItems },
          { params: { country: formData.country } }
        );
        
        setCartTotals(response.data);
      } catch (err) {
        setError(language === 'it' 
          ? 'Errore nel calcolo del carrello' 
          : 'Failed to calculate cart');
      }
    };
    
    calculateTotals();
  }, [cart, formData.country, language]);

  // Reset payment method if COD is selected but country is not IT
  useEffect(() => {
    if (paymentMethod === 'cod' && formData.country !== 'IT') {
      setPaymentMethod('stripe');
      setCodAccepted(false);
    }
  }, [formData.country, paymentMethod]);

  // Google Ads remarketing: begin_checkout event (fires once per checkout session)
  const beginCheckoutFiredRef = useRef(false);
  useEffect(() => {
    if (beginCheckoutFiredRef.current) return;
    if (cart.length === 0) return;
    if (!cartTotals?.total) return;
    trackBeginCheckout(cart, cartTotals.total);
    beginCheckoutFiredRef.current = true;
  }, [cart, cartTotals]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;
    setCouponLoading(true);
    setCouponError('');
    setCouponData(null);
    try {
      const response = await axios.post(`${API_URL}/api/coupons/validate`, {
        code: couponCode.trim(),
        subtotal: cartTotals?.subtotal || 0
      });
      setCouponData(response.data);
    } catch (err) {
      setCouponError(err.response?.data?.detail || (language === 'it' ? 'Codice non valido' : 'Invalid code'));
    } finally {
      setCouponLoading(false);
    }
  };

  const removeCoupon = () => {
    setCouponData(null);
    setCouponCode('');
    setCouponError('');
  };

  const validateForm = () => {
    if (!formData.name || !formData.email || !formData.phone || 
        !formData.address || !formData.city || !formData.country) {
      setError(language === 'it' 
        ? 'Compila tutti i campi obbligatori' 
        : 'Please fill all required fields');
      return false;
    }
    
    if (paymentMethod === 'cod' && !codAccepted) {
      setError(language === 'it' 
        ? 'Devi accettare il supplemento di €4.00 per il contrassegno' 
        : 'You must accept the €4.00 COD fee');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    setError('');
    
    try {
      // Step 1: Create order
      const cartItems = cart.map(item => ({
        sku: item.sku,
        quantity: item.quantity
      }));
      
      const orderResponse = await axios.post(`${API_URL}/api/orders`, {
        items: cartItems,
        shipping_address: formData,
        notes: formData.notes,
        coupon_code: couponData?.code || null
      });
      
      const newOrderId = orderResponse.data.order_id;
      setOrderId(newOrderId);

      // Mark coupon as used
      if (couponData?.code) {
        await axios.post(`${API_URL}/api/coupons/use/${couponData.code}`).catch(() => {});
      }
      
      // Step 2: Process payment based on method
      const origin = window.location.origin;
      
      switch (paymentMethod) {
        case 'stripe':
          await handleStripePayment(newOrderId, origin);
          break;
          
        case 'paypal':
          await handlePayPalPayment(newOrderId, origin);
          break;
          
        case 'cod':
          await handleCODPayment(newOrderId, origin);
          break;
          
        case 'bank_transfer':
          await handleBankTransferPayment(newOrderId, origin);
          break;
          
        default:
          throw new Error('Invalid payment method');
      }
      
    } catch (err) {
      setError(err.response?.data?.detail || (language === 'it' 
        ? 'Errore durante il checkout' 
        : 'Checkout error'));
      setLoading(false);
    }
  };

  const handleStripePayment = async (orderId, origin) => {
    try {
      const response = await axios.post(`${API_URL}/api/payments/stripe/checkout`, {
        order_id: orderId,
        payment_method: { type: 'stripe' },
        origin_url: origin
      });
      
      if (response.data.success) {
        // Redirect to Stripe
        window.location.href = response.data.checkout_url;
      }
    } catch (err) {
      throw err;
    }
  };

  const handlePayPalPayment = async (orderId, origin) => {
    try {
      const response = await axios.post(`${API_URL}/api/payments/paypal/checkout`, {
        order_id: orderId,
        payment_method: { type: 'paypal' },
        origin_url: origin
      });
      
      if (response.data.success) {
        // Redirect to PayPal
        window.location.href = response.data.approval_url;
      }
    } catch (err) {
      throw err;
    }
  };

  const handleCODPayment = async (orderId, origin) => {
    try {
      const response = await axios.post(`${API_URL}/api/payments/cod`, {
        order_id: orderId,
        payment_method: { 
          type: 'cod',
          cod_fee: 4.00
        },
        origin_url: origin
      });
      
      if (response.data.success) {
        clearCart();
        navigate(`/checkout/success?order_id=${orderId}&method=cod`);
      }
    } catch (err) {
      throw err;
    }
  };

  const handleBankTransferPayment = async (orderId, origin) => {
    try {
      const response = await axios.post(`${API_URL}/api/payments/bank-transfer`, {
        order_id: orderId,
        payment_method: { 
          type: 'bank_transfer',
          transfer_type: transferType
        },
        origin_url: origin
      });
      
      if (response.data.success) {
        clearCart();
        navigate(`/checkout/success?order_id=${orderId}&method=bank_transfer&type=${transferType}`, {
          state: { bankDetails: response.data.bank_details }
        });
      }
    } catch (err) {
      throw err;
    }
  };

  if (cart.length === 0 && !orderId) {
    return (
      <div className="dark-container pt-20 min-h-screen">
        <div className="max-w-4xl mx-auto px-6 py-20 text-center">
          <h1 className="display-medium mb-4">
            {language === 'it' ? 'Carrello Vuoto' : 'Empty Cart'}
          </h1>
          <button onClick={() => navigate('/products')} className="btn-primary">
            {language === 'it' ? 'Torna ai Prodotti' : 'Back to Products'}
          </button>
        </div>
      </div>
    );
  }

  const countries = [
    { code: 'IT', name_it: 'Italia', name_en: 'Italy' },
    // EU Principali
    { code: 'FR', name_it: 'Francia', name_en: 'France' },
    { code: 'DE', name_it: 'Germania', name_en: 'Germany' },
    { code: 'ES', name_it: 'Spagna', name_en: 'Spain' },
    { code: 'AT', name_it: 'Austria', name_en: 'Austria' },
    { code: 'BE', name_it: 'Belgio', name_en: 'Belgium' },
    { code: 'NL', name_it: 'Paesi Bassi', name_en: 'Netherlands' },
    { code: 'PT', name_it: 'Portogallo', name_en: 'Portugal' },
    { code: 'GR', name_it: 'Grecia', name_en: 'Greece' },
    { code: 'IE', name_it: 'Irlanda', name_en: 'Ireland' },
    { code: 'LU', name_it: 'Lussemburgo', name_en: 'Luxembourg' },
    // Altri paesi EU
    { code: 'FI', name_it: 'Finlandia', name_en: 'Finland' },
    { code: 'SE', name_it: 'Svezia', name_en: 'Sweden' },
    { code: 'DK', name_it: 'Danimarca', name_en: 'Denmark' },
    { code: 'PL', name_it: 'Polonia', name_en: 'Poland' },
    { code: 'CZ', name_it: 'Repubblica Ceca', name_en: 'Czech Republic' },
    { code: 'HU', name_it: 'Ungheria', name_en: 'Hungary' },
    { code: 'RO', name_it: 'Romania', name_en: 'Romania' },
    { code: 'BG', name_it: 'Bulgaria', name_en: 'Bulgaria' },
    { code: 'HR', name_it: 'Croazia', name_en: 'Croatia' },
    { code: 'SI', name_it: 'Slovenia', name_en: 'Slovenia' },
    { code: 'SK', name_it: 'Slovacchia', name_en: 'Slovakia' },
    { code: 'EE', name_it: 'Estonia', name_en: 'Estonia' },
    { code: 'LV', name_it: 'Lettonia', name_en: 'Latvia' },
    { code: 'LT', name_it: 'Lituania', name_en: 'Lithuania' },
    { code: 'CY', name_it: 'Cipro', name_en: 'Cyprus' },
    { code: 'MT', name_it: 'Malta', name_en: 'Malta' },
    // Extra-EU
    { code: 'AR', name_it: 'Argentina', name_en: 'Argentina' },
    { code: 'US', name_it: 'Stati Uniti', name_en: 'United States' },
    { code: 'CA', name_it: 'Canada', name_en: 'Canada' },
    { code: 'AU', name_it: 'Australia', name_en: 'Australia' },
    { code: 'GB', name_it: 'Regno Unito', name_en: 'United Kingdom' },
    { code: 'CH', name_it: 'Svizzera', name_en: 'Switzerland' }
  ];

  return (
    <div className="dark-container pt-20 min-h-screen">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-[7.6923%] py-8">
        {/* Header */}
        <button 
          onClick={() => navigate('/cart')}
          className="flex items-center gap-2 text-white/80 hover:text-white transition-colors mb-6"
        >
          <ArrowLeft size={20} />
          {language === 'it' ? 'Torna al Carrello' : 'Back to Cart'}
        </button>

        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-8">
          {language === 'it' ? 'Checkout' : 'Checkout'}
        </h1>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Form Section */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Shipping Information */}
              <div className="bg-white/5 rounded-lg p-6 border border-white/10">
                <h2 className="text-2xl font-bold mb-4">
                  {language === 'it' ? 'Informazioni di Spedizione' : 'Shipping Information'}
                </h2>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm mb-2">
                      {language === 'it' ? 'Nome Completo' : 'Full Name'} *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full bg-black/30 border border-white/20 rounded px-4 py-2 focus:outline-none focus:border-primary"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm mb-2">Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full bg-black/30 border border-white/20 rounded px-4 py-2 focus:outline-none focus:border-primary"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm mb-2">
                      {language === 'it' ? 'Telefono' : 'Phone'} *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full bg-black/30 border border-white/20 rounded px-4 py-2 focus:outline-none focus:border-primary"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm mb-2">
                      {language === 'it' ? 'Paese' : 'Country'} *
                    </label>
                    <select
                      name="country"
                      value={formData.country}
                      onChange={handleInputChange}
                      className="w-full bg-black/30 border border-white/20 rounded px-4 py-2 focus:outline-none focus:border-primary"
                      required
                    >
                      {countries.map(country => (
                        <option key={country.code} value={country.code}>
                          {language === 'it' ? country.name_it : country.name_en}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="md:col-span-2">
                    <label className="block text-sm mb-2">
                      {language === 'it' ? 'Indirizzo' : 'Address'} *
                    </label>
                    <input
                      type="text"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      className="w-full bg-black/30 border border-white/20 rounded px-4 py-2 focus:outline-none focus:border-primary"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm mb-2">
                      {language === 'it' ? 'Città' : 'City'} *
                    </label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      className="w-full bg-black/30 border border-white/20 rounded px-4 py-2 focus:outline-none focus:border-primary"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm mb-2">
                      {language === 'it' ? 'Provincia' : 'Province'}
                    </label>
                    <input
                      type="text"
                      name="province"
                      value={formData.province}
                      onChange={handleInputChange}
                      className="w-full bg-black/30 border border-white/20 rounded px-4 py-2 focus:outline-none focus:border-primary"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm mb-2">
                      {language === 'it' ? 'CAP' : 'Postal Code'}
                    </label>
                    <input
                      type="text"
                      name="postal_code"
                      value={formData.postal_code}
                      onChange={handleInputChange}
                      className="w-full bg-black/30 border border-white/20 rounded px-4 py-2 focus:outline-none focus:border-primary"
                    />
                  </div>
                  
                  <div className="md:col-span-2">
                    <label className="block text-sm mb-2">
                      {language === 'it' ? 'Note Ordine (opzionale)' : 'Order Notes (optional)'}
                    </label>
                    <textarea
                      name="notes"
                      value={formData.notes}
                      onChange={handleInputChange}
                      rows={3}
                      className="w-full bg-black/30 border border-white/20 rounded px-4 py-2 focus:outline-none focus:border-primary"
                    />
                  </div>
                </div>
              </div>

              {/* Payment Method */}
              <div className="bg-white/5 rounded-lg p-6 border border-white/10">
                <h2 className="text-2xl font-bold mb-4">
                  {language === 'it' ? 'Metodo di Pagamento' : 'Payment Method'}
                </h2>
                
                <div className="space-y-3">
                  {/* Stripe */}
                  <label className="flex items-start gap-3 p-4 border border-white/20 rounded cursor-pointer hover:border-primary transition-colors">
                    <input
                      type="radio"
                      name="payment_method"
                      value="stripe"
                      checked={paymentMethod === 'stripe'}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <CreditCard size={20} className="text-primary" />
                        <span className="font-semibold" data-testid="stripe-method-label">
                          {{ it: 'Carta, Google Pay o Amazon Pay', es: 'Tarjeta, Google Pay o Amazon Pay' }[language] || 'Card, Google Pay or Amazon Pay'}
                        </span>
                      </div>
                      <p className="text-sm text-white/60 mt-1">
                        {{ it: 'Pagamento sicuro tramite Stripe', es: 'Pago seguro con Stripe' }[language] || 'Secure payment via Stripe'}
                      </p>
                    </div>
                  </label>

                  {/* PayPal */}
                  <label className="flex items-start gap-3 p-4 border border-white/20 rounded cursor-pointer hover:border-primary transition-colors">
                    <input
                      type="radio"
                      name="payment_method"
                      value="paypal"
                      checked={paymentMethod === 'paypal'}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">PayPal</span>
                      </div>
                      <p className="text-sm text-white/60 mt-1">
                        {language === 'it' ? 'Paga con il tuo account PayPal' : 'Pay with your PayPal account'}
                      </p>
                    </div>
                  </label>

                  {/* Cash on Delivery - SOLO per Italia */}
                  {formData.country === 'IT' && (
                    <label className="flex items-start gap-3 p-4 border border-white/20 rounded cursor-pointer hover:border-primary transition-colors">
                      <input
                        type="radio"
                        name="payment_method"
                        value="cod"
                        checked={paymentMethod === 'cod'}
                        onChange={(e) => setPaymentMethod(e.target.value)}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold">
                            {language === 'it' ? 'Contrassegno' : 'Cash on Delivery'}
                          </span>
                          <span className="text-sm text-primary">+€4.00</span>
                        </div>
                        <p className="text-sm text-white/60 mt-1">
                          {language === 'it' ? 'Paga alla consegna (supplemento €4.00)' : 'Pay on delivery (€4.00 fee)'}
                        </p>
                        
                        {paymentMethod === 'cod' && (
                          <label className="flex items-center gap-2 mt-3">
                            <input
                              type="checkbox"
                              checked={codAccepted}
                              onChange={(e) => setCodAccepted(e.target.checked)}
                              className="rounded"
                            />
                            <span className="text-sm">
                              {language === 'it' 
                                ? 'Accetto il supplemento di €4.00 per il contrassegno' 
                                : 'I accept the €4.00 COD fee'}
                            </span>
                          </label>
                        )}
                      </div>
                    </label>
                  )}

                  {/* Bank Transfer */}
                  <label className="flex items-start gap-3 p-4 border border-white/20 rounded cursor-pointer hover:border-primary transition-colors">
                    <input
                      type="radio"
                      name="payment_method"
                      value="bank_transfer"
                      checked={paymentMethod === 'bank_transfer'}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">
                          {language === 'it' ? 'Bonifico Bancario' : 'Bank Transfer'}
                        </span>
                      </div>
                      <p className="text-sm text-white/60 mt-1">
                        {language === 'it' ? 'Bonifico bancario istantaneo o ordinario' : 'Instant or standard bank transfer'}
                      </p>
                      
                      {paymentMethod === 'bank_transfer' && (
                        <div className="mt-3 space-y-2">
                          <label className="flex items-center gap-2">
                            <input
                              type="radio"
                              name="transfer_type"
                              value="instant"
                              checked={transferType === 'instant'}
                              onChange={(e) => setTransferType(e.target.value)}
                            />
                            <span className="text-sm">
                              {language === 'it' ? 'Bonifico Istantaneo (conferma immediata)' : 'Instant Transfer (immediate confirmation)'}
                            </span>
                          </label>
                          <label className="flex items-center gap-2">
                            <input
                              type="radio"
                              name="transfer_type"
                              value="standard"
                              checked={transferType === 'standard'}
                              onChange={(e) => setTransferType(e.target.value)}
                            />
                            <span className="text-sm">
                              {language === 'it' ? 'Bonifico Ordinario (in attesa di conferma)' : 'Standard Transfer (awaiting confirmation)'}
                            </span>
                          </label>
                        </div>
                      )}
                    </div>
                  </label>
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-4 flex items-start gap-3">
                  <AlertCircle size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-red-500">{error}</p>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full btn-primary flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 size={20} className="animate-spin" />
                    {language === 'it' ? 'Elaborazione...' : 'Processing...'}
                  </>
                ) : (
                  <>
                    {language === 'it' ? 'Completa Ordine' : 'Complete Order'}
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white/5 rounded-lg p-6 border border-white/10 sticky top-24">
              <h2 className="text-2xl font-bold mb-4">
                {language === 'it' ? 'Riepilogo Ordine' : 'Order Summary'}
              </h2>
              
              {/* Cart Items */}
              <div className="space-y-3 mb-4 max-h-60 overflow-y-auto">
                {cart.map(item => (
                  <div key={item.sku} className="flex gap-3">
                    {item.image && (
                      <img 
                        src={getProxiedImageUrl(item.image)} 
                        alt={language === 'it' ? item.name_it : item.name_en}
                        className="w-16 h-16 object-cover rounded"
                      />
                    )}
                    <div className="flex-1">
                      <p className="text-sm font-medium">
                        {language === 'it' ? item.name_it : item.name_en}
                      </p>
                      <p className="text-xs text-white/60">
                        {language === 'it' ? 'Quantità' : 'Qty'}: {item.quantity}
                      </p>
                      <p className="text-sm text-primary">
                        €{(item.price * item.quantity).toFixed(2)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Coupon Code */}
              <div className="border-t border-white/10 pt-4 mb-4">
                <label className="block text-sm font-semibold mb-2">
                  {language === 'it' ? 'Codice Sconto' : 'Discount Code'}
                </label>
                {couponData ? (
                  <div className="flex items-center justify-between bg-green-500/10 border border-green-500/30 rounded px-3 py-2" data-testid="coupon-applied">
                    <div>
                      <span className="text-green-400 font-semibold text-sm">{couponData.code}</span>
                      <span className="text-green-400 text-sm ml-2">{couponData.discount_label}</span>
                    </div>
                    <button onClick={removeCoupon} className="text-red-400 hover:text-red-300 text-sm font-semibold" data-testid="remove-coupon-btn">
                      {language === 'it' ? 'Rimuovi' : 'Remove'}
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={couponCode}
                      onChange={(e) => { setCouponCode(e.target.value.toUpperCase()); setCouponError(''); }}
                      placeholder={language === 'it' ? 'Inserisci codice...' : 'Enter code...'}
                      className="flex-1 bg-black/30 border border-white/20 rounded px-3 py-2 text-sm focus:outline-none focus:border-primary uppercase"
                      data-testid="coupon-input"
                    />
                    <button
                      type="button"
                      onClick={handleApplyCoupon}
                      disabled={couponLoading || !couponCode.trim()}
                      className="btn-primary text-sm px-4 py-2"
                      data-testid="apply-coupon-btn"
                    >
                      {couponLoading ? '...' : (language === 'it' ? 'Applica' : 'Apply')}
                    </button>
                  </div>
                )}
                {couponError && (
                  <p className="text-red-400 text-xs mt-1" data-testid="coupon-error">{couponError}</p>
                )}
              </div>
              
              <div className="border-t border-white/10 pt-4 space-y-2">
                <div className="flex justify-between">
                  <span>{language === 'it' ? 'Subtotale' : 'Subtotal'}</span>
                  <span>€{cartTotals?.subtotal.toFixed(2) || '0.00'}</span>
                </div>
                
                <div className="flex justify-between">
                  <span>{language === 'it' ? 'Spedizione' : 'Shipping'}</span>
                  <span>
                    {cartTotals?.free_shipping 
                      ? (language === 'it' ? 'GRATIS' : 'FREE')
                      : `€${cartTotals?.shipping.toFixed(2) || '0.00'}`
                    }
                  </span>
                </div>
                
                {couponData && (
                  <div className="flex justify-between text-green-400" data-testid="discount-row">
                    <span>{language === 'it' ? 'Sconto' : 'Discount'} ({couponData.discount_label})</span>
                    <span>-€{couponData.discount_amount.toFixed(2)}</span>
                  </div>
                )}
                
                {paymentMethod === 'cod' && codAccepted && (
                  <div className="flex justify-between text-primary">
                    <span>{language === 'it' ? 'Supplemento Contrassegno' : 'COD Fee'}</span>
                    <span>€4.00</span>
                  </div>
                )}
                
                <div className="flex justify-between text-xl font-bold pt-2 border-t border-white/10">
                  <span>{language === 'it' ? 'Totale' : 'Total'}</span>
                  <span className="text-primary">
                    €{(
                      (cartTotals?.total || 0) + 
                      (paymentMethod === 'cod' && codAccepted ? 4.00 : 0) -
                      (couponData?.discount_amount || 0)
                    ).toFixed(2)}
                  </span>
                </div>
              </div>
              
              {cartTotals && !cartTotals.free_shipping && (
                <p className="text-xs text-white/60 mt-4">
                  {language === 'it' 
                    ? `Spedizione gratuita per ordini superiori a €${cartTotals.free_shipping_threshold.toFixed(2)} (solo Italia)` 
                    : language === 'es'
                    ? `Envío gratis para pedidos superiores a €${cartTotals.free_shipping_threshold.toFixed(2)} (solo Italia)`
                    : `Free shipping on orders over €${cartTotals.free_shipping_threshold.toFixed(2)} (Italy only)`
                  }
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
